import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Search, 
  ShoppingCart, 
  Trash2, 
  Plus, 
  Minus, 
  CreditCard, 
  DollarSign, 
  User, 
  Hash,
  Check,
  CheckCircle,  X,
  Receipt,
  AlertCircle,
  Zap,
  Clock,
  ArrowLeft,
  Calculator,
  Settings,
  Smartphone,
  QrCode,
  TrendingUp,
  Package,
  Users,
  BarChart3
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { dataHelpers } from '../../lib/supabase';
import { asaasHelpers } from '../../lib/asaasHelpers';
import QRCodeGenerator from '../../components/ui/QRCodeGeneratorNew';
import toast from 'react-hot-toast';

const PDVPage = () => {
  const navigate = useNavigate();
  const { company, person, user } = useAuth();
  const sellerId = person?.id || user?.id;
  
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchType, setSearchType] = useState('nome');
  const [cart, setCart] = useState([]);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [paymentMethod, setPaymentMethod] = useState('saldo');
  const [showConfirmation, setShowConfirmation] = useState(false);  const [searchResults, setSearchResults] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('Todos');
  const [showCalculator, setShowCalculator] = useState(false);
  const [cashReceived, setCashReceived] = useState('');
  const [printVoucherEnabled, setPrintVoucherEnabled] = useState(true);
  const [lastSaleData, setLastSaleData] = useState(null);
  const [lastStudent, setLastStudent] = useState(null);  const [showVoucherPreview, setShowVoucherPreview] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);  // Estados relacionados ao PIX
  const [pixPaymentData, setPixPaymentData] = useState(null);
  const [pixStatus, setPixStatus] = useState(null);
  const [isGeneratingPix, setIsGeneratingPix] = useState(false);
  const [pixError, setPixError] = useState(null);
  const [showPixModal, setShowPixModal] = useState(false);
  
  // Chave para persistência PIX no PDV
  const pdvPixStorageKey = `pdv_pending_pix_${sellerId}_${company?.id}`;

  // Carregar PIX pendente do localStorage na inicialização
  useEffect(() => {
    if (!sellerId || !company?.id) return;
    
    try {
      const stored = localStorage.getItem(pdvPixStorageKey);
      if (stored) {
        const pixData = JSON.parse(stored);
        
        // Verificar se o PIX não expirou (10 minutos)
        const createdAt = new Date(pixData.createdAt);
        const now = new Date();
        const diffMinutes = (now - createdAt) / (1000 * 60);
        
        if (diffMinutes < 10) {
          console.log('🔄 PIX PDV pendente encontrado:', pixData);
          setPixPaymentData(pixData);
          setPixStatus('pending');
        } else {
          console.log('⏰ PIX PDV expirado, removendo');
          localStorage.removeItem(pdvPixStorageKey);
        }
      }
    } catch (error) {
      console.error('❌ Erro ao carregar PIX PDV:', error);
      localStorage.removeItem(pdvPixStorageKey);
    }
  }, [sellerId, company?.id]);

  // Salvar PIX pendente
  const savePendingPixPDV = (pixData) => {
    try {
      const dataToSave = {
        ...pixData,
        createdAt: new Date().toISOString()
      };
      localStorage.setItem(pdvPixStorageKey, JSON.stringify(dataToSave));
      console.log('💾 PIX PDV salvo:', dataToSave);
    } catch (error) {
      console.error('❌ Erro ao salvar PIX PDV:', error);
    }
  };

  // Limpar PIX pendente
  const clearPendingPixPDV = () => {
    try {
      localStorage.removeItem(pdvPixStorageKey);
      console.log('🗑️ PIX PDV removido do localStorage');
    } catch (error) {
      console.error('❌ Erro ao limpar PIX PDV:', error);
    }
  };

  const searchInputRef = useRef(null);

  useEffect(() => {
    if (paymentMethod === 'saldo') {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  }, [paymentMethod]);
  
  // Real data from database
  const [products, setProducts] = useState([]);
  const [students, setStudents] = useState([]);
  const [currentCashRegister, setCurrentCashRegister] = useState(null);
  const [loading, setLoading] = useState(false);

  // Atualizar relógio
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Load data from database
  useEffect(() => {
    if (company?.id) {
      loadData();
    }
  }, [company]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [productsData, studentsData, cashRegisterData] = await Promise.all([
        dataHelpers.getProducts(company.id),
        dataHelpers.getStudents(company.id),
        dataHelpers.getCurrentCashRegister(company.id)
      ]);
      
      setProducts(productsData || []);
      setStudents(studentsData || []);
      setCurrentCashRegister(cashRegisterData);
    } catch (error) {
      console.error('Error loading PDV data:', error);
      toast.error('Erro ao carregar dados do PDV');
    } finally {
      setLoading(false);
    }
  };

  // Get categories from products
  const getCategories = () => {
    const uniqueCategories = [...new Set(products.map(p => p.category))].filter(Boolean);
    return [
      { name: 'Todos', icon: Package, color: 'bg-gray-500' },
      ...uniqueCategories.map(cat => ({
        name: cat,
        icon: Package,
        color: 'bg-blue-500'
      }))
    ];
  };

  const categories = getCategories();
  
  // Filtrar produtos
  const filteredProducts = selectedCategory === 'Todos' 
    ? products 
    : products.filter(product => product.category === selectedCategory);

  // Buscar alunos
  const searchStudents = (term) => {
    if (!term) {
      setSearchResults([]);
      return;
    }    const results = students.filter(student => {
      const studentName = student.people?.name || student.name || '';
      const registration = student.registration_number || '';
        switch (searchType) {
        case 'nome':
          return studentName.toLowerCase().includes(term.toLowerCase());
        case 'carteirinha':
          return registration.includes(term);
        case 'matricula':
          return registration.toLowerCase().includes(term.toLowerCase());
        default:
          return false;
      }
    }).map(student => ({
      // Mapear dados para formato esperado pelo PDV
      ...student,
      id: student.id,
      nome: student.people?.name || student.name || '',
      turma: student.class_name || '',
      carteirinha: student.registration_number || '',
      saldo: student.balance || 0,
      email: student.people?.email || student.email || ''
    }));

    setSearchResults(results);
  };

  useEffect(() => {
    searchStudents(searchTerm);
  }, [searchTerm, searchType]);

  // Funções do carrinho
  const selectStudent = (student) => {
    setSelectedStudent(student);
    setSearchTerm('');
    setSearchResults([]);
  };

  const addToCart = (product) => {
    const existingItem = cart.find(item => item.id === product.id);
    
    if (existingItem) {
      setCart(cart.map(item => 
        item.id === product.id 
          ? { ...item, quantidade: item.quantidade + 1 }
          : item
      ));
    } else {
      setCart([...cart, { 
        ...product, 
        quantidade: 1,
        // Normalize field names for consistency
        nome: product.name,
        preco: product.price,
        categoria: product.category,
        estoque: product.stock_quantity,
        descricao: product.description || '',
        foto: product.image_url ? null : '📦' // Use emoji if no image
      }]);
    }
    
    toast.success(`${product.name} adicionado ao carrinho`);
  };

  const removeFromCart = (productId) => {
    setCart(cart.filter(item => item.id !== productId));
  };

  const updateQuantity = (productId, newQuantity) => {
    if (newQuantity <= 0) {
      removeFromCart(productId);
      return;
    }

    setCart(cart.map(item => 
      item.id === productId 
        ? { ...item, quantidade: newQuantity }
        : item
    ));
  };

  const getTotal = () => {
    return cart.reduce((total, item) => total + (item.preco * item.quantidade), 0);
  };

  const clearCart = () => {
    setCart([]);
  };  const finalizeSale = () => {
    if (cart.length === 0) {
      alert('Adicione produtos ao carrinho!');
      return;
    }

    setShowConfirmation(true);
    
    // Verificar se há PIX pendente e restaurar automaticamente
    if (pixPaymentData && pixStatus === 'pending' && paymentMethod === 'pix') {
      console.log('🔄 Restaurando PIX pendente automaticamente:', pixPaymentData);
      setShowPixModal(true);
    }
  };

  // Função para gerar PIX
  const generatePix = async () => {
    if (isGeneratingPix) return;
    
    try {
      setIsGeneratingPix(true);
      setPixError(null);
      setShowPixModal(true);
      
      const total = getTotal();
      
      // Buscar o ID do cliente "Consumidor Final" das configurações PIX
      let customerId = null;
      try {
        const pixSettings = await dataHelpers.getPixSettings(company?.id);
        customerId = pixSettings?.default_customer_id || null;
        console.log('🔍 Cliente "Consumidor Final" encontrado:', customerId);
      } catch (error) {
        console.warn('⚠️ Erro ao buscar configurações PIX, usando fallback:', error);
      }

      // Dados do pagamento PIX para PDV
      const pixData = {
        value: total,
        description: `Venda PDV - ${cart.map(item => `${item.quantidade}x ${item.nome}`).join(', ')}`,
        dueDate: new Date(Date.now() + 10 * 60 * 1000).toISOString(), // 10 minutos
        company_id: company?.id
      };

      // Se temos o cliente configurado, usar ele. Senão, enviar dados para criar/buscar
      if (customerId) {
        pixData.customer = customerId;
        console.log('✅ Usando cliente "Consumidor Final" configurado:', customerId);
      } else {
        // Fallback: enviar dados da empresa para a Edge Function criar/buscar cliente
        pixData.customer = {
          name: 'Consumidor Final',
          cpfCnpj: company?.document || '00000000000',
          email: company?.email || 'consumidor.final@cantinago.com'
        };
        console.log('⚠️ Cliente não configurado, usando fallback com dados da empresa');
      }

      console.log('🏗️ Gerando PIX para PDV com dados:', pixData);
      
      const result = await asaasHelpers.createPixPayment(pixData);
      
      if (result?.success && result?.payment) {
        const paymentData = {
          ...result.payment,
          createdAt: new Date().toISOString(),
          cartItems: cart,
          total: total
        };
        
        setPixPaymentData(paymentData);
        setPixStatus('pending');
        
        // Salvar PIX pendente
        savePendingPixPDV(paymentData);
        
        console.log('✅ PIX gerado com sucesso:', paymentData);
        toast.success('PIX gerado com sucesso!');
      } else {
        throw new Error(result?.error || 'Erro ao gerar PIX');
      }
    } catch (error) {
      console.error('❌ Erro ao gerar PIX:', error);
      setPixError(error.message || 'Erro ao gerar PIX');
      toast.error('Erro ao gerar PIX: ' + (error.message || 'Erro desconhecido'));
      setShowPixModal(false);
    } finally {
      setIsGeneratingPix(false);
    }
  };
  // Função para cancelar PIX
  const cancelPix = async () => {
    try {
      if (pixPaymentData?.id) {
        console.log('🚫 Cancelando PIX:', pixPaymentData.id);
        await asaasHelpers.cancelPixPayment(pixPaymentData.id, company?.id);
      }
        setPixPaymentData(null);
      setPixStatus(null);
      setPixError(null);
      setShowPixModal(false);
      clearPendingPixPDV();
      
      toast.success('PIX cancelado com sucesso!');
    } catch (error) {
      console.error('❌ Erro ao cancelar PIX:', error);
      toast.error('Erro ao cancelar PIX');
    }
  };

  // Função para verificar status do PIX
  const checkPixStatus = async () => {
    if (!pixPaymentData?.id) return;
    
    try {
      const status = await asaasHelpers.checkPaymentStatus(pixPaymentData.id, company?.id);
      console.log('🔍 Status do PIX:', status);
        if (status?.status === 'RECEIVED') {
        setPixStatus('paid');
        clearPendingPixPDV();
        toast.success('Pagamento PIX confirmado!');
        
        // Finalizar venda automaticamente
        await confirmSaleWithPix();      } else if (status?.status === 'OVERDUE') {
        setPixStatus('expired');
        clearPendingPixPDV();
        toast.error('PIX expirado');
      }
    } catch (error) {
      console.error('❌ Erro ao verificar status do PIX:', error);
    }
  };

  // Função para confirmar venda após pagamento PIX
  const confirmSaleWithPix = async () => {
    if (processingPayment) return;
    
    try {
      setProcessingPayment(true);
      
      const total = getTotal();
      
      // Preparar dados da venda
      const saleData = {
        seller_id: sellerId,
        items: cart.map(item => ({
          product_id: item.id,
          quantity: item.quantidade,
          unit_price: item.preco,
          total_price: item.preco * item.quantidade
        })),
        payment_method: 'pix',
        total_amount: total,
        amount_paid: total,
        change_amount: 0,
        notes: 'Venda PDV - Pagamento via PIX',
        pix_payment_id: pixPaymentData?.id
      };

      console.log('Creating PIX sale with data:', saleData);
      await dataHelpers.createSale(company.id, saleData);
      
      // Atualizar estatísticas do caixa
      if (currentCashRegister) {
        await updateCashRegisterStats(saleData);
      }

      toast.success('Venda PIX realizada com sucesso!');
      
      // Salvar dados da última venda
      setLastSaleData(saleData);
      setLastStudent(null);
      
      // Gerar comprovante se habilitado
      if (printVoucherEnabled) {
        await printVoucher(saleData, null);
      }
      
      // Limpar dados
      setCart([]);
      setShowConfirmation(false);      setShowPixModal(false);
      setPixPaymentData(null);
      setPixStatus(null);
      clearPendingPixPDV();
      
    } catch (error) {
      console.error('Error processing PIX sale:', error);
      toast.error('Erro ao processar venda PIX: ' + error.message);
    } finally {
      setProcessingPayment(false);
    }
  };

  // Função para copiar código PIX
  const copyPixCode = async () => {
    if (pixPaymentData?.payload) {
      try {
        await navigator.clipboard.writeText(pixPaymentData.payload);
        toast.success('Código PIX copiado!');
      } catch (error) {
        console.error('Erro ao copiar código PIX:', error);
        toast.error('Erro ao copiar código');
      }
    }
  };

  // Monitorar status do PIX quando ativo
  useEffect(() => {
    if (pixStatus === 'pending' && pixPaymentData?.id) {
      const interval = setInterval(checkPixStatus, 3000); // Verificar a cada 3 segundos
      return () => clearInterval(interval);
    }
  }, [pixStatus, pixPaymentData?.id]);  const confirmSale = async () => {
    if (processingPayment) return;
    
    const total = getTotal();
    
    // Se for PIX, gerar PIX primeiro
    if (paymentMethod === 'pix') {
      await generatePix();
      return; // Não prosseguir com a venda ainda
    }
    
    // Verificar se há caixa aberto para vendas em dinheiro
    if (paymentMethod === 'dinheiro' && !currentCashRegister) {
      toast.error('É necessário abrir o caixa antes de realizar vendas em dinheiro!');
      return;
    }
      // Validações finais
    if (paymentMethod === 'saldo' && (!selectedStudent || selectedStudent.saldo < total)) {
      toast.error('Selecione um aluno com saldo suficiente!');
      return;
    }    if (paymentMethod === 'dinheiro') {
      const received = parseFloat(cashReceived) || 0;
      if (received < total) {
        toast.error('Valor recebido insuficiente!');
        return;
      }
    }

    // Para cartão, apenas seguir o fluxo normal
    if (paymentMethod === 'cartao') {
      // Cartão será processado normalmente
    }
    
    try {
      setProcessingPayment(true);
        // Preparar dados da venda
      const saleData = {
        seller_id: person?.id || user?.id || null, // ID do vendedor (pessoa autenticada)
        items: cart.map(item => ({
          product_id: item.id,
          quantity: item.quantidade,
          unit_price: item.preco,
          total_price: item.preco * item.quantidade
        })),
        payment_method: paymentMethod === 'saldo' ? 'student_balance' : paymentMethod === 'dinheiro' ? 'cash' : paymentMethod === 'cartao' ? 'card' : paymentMethod === 'pix' ? 'pix' : 'cash',
        total_amount: total,
        amount_paid: paymentMethod === 'dinheiro' ? parseFloat(cashReceived) : total,
        change_amount: paymentMethod === 'dinheiro' ? calculateChange() : 0,
        notes: selectedStudent ? `Venda para aluno: ${selectedStudent.nome}` : 'Consumidor final'
      };

      // Adicionar student_id apenas se houver aluno selecionado
      if (selectedStudent?.id) {
        saleData.student_id = selectedStudent.id;
      }      // Criar venda
      console.log('Creating sale with data:', saleData);
      await dataHelpers.createSale(company.id, saleData);
      
      // Atualizar estatísticas do caixa se a venda foi realizada
      if (currentCashRegister) {
        console.log('Updating cash register stats for sale:', saleData);
        console.log('Current cash register:', currentCashRegister);
        await updateCashRegisterStats(saleData);
      } else {
        console.log('No cash register open, skipping stats update');
      }
        // Se foi pago com saldo do aluno, atualizar saldo
      if (paymentMethod === 'saldo' && selectedStudent) {
        const newBalance = (parseFloat(selectedStudent.saldo) || 0) - total;
        await dataHelpers.updateStudent(selectedStudent.id, { balance: newBalance });
        
        // Atualizar localmente
        setSelectedStudent({
          ...selectedStudent,
          saldo: newBalance,
          balance: newBalance
        });
        
        // Recarregar dados dos alunos
        const updatedStudents = await dataHelpers.getStudents(company.id);
        setStudents(updatedStudents || []);
      }

      toast.success('Venda realizada com sucesso!');
      
      // Salvar dados da última venda para possível reimpressão
      setLastSaleData(saleData);
      setLastStudent(selectedStudent);
      
      // Gerar e imprimir comprovante se habilitado
      if (printVoucherEnabled) {
        await printVoucher(saleData, selectedStudent);
      }
      
      // Limpar dados
      setCart([]);
      setShowConfirmation(false);
      setShowCalculator(false);
      setCashReceived('');
      setSelectedStudent(null);
      setSearchTerm('');
      setSearchResults([]);
        } catch (error) {
      console.error('Error processing sale:', error);
      console.error('Error details:', {
        message: error.message,
        code: error.code,
        details: error.details,
        hint: error.hint
      });
      
      // Mensagem de erro mais específica
      let errorMessage = 'Erro ao processar venda';
      if (error.message?.includes('relation "sales" does not exist')) {
        errorMessage = 'Tabela de vendas não encontrada. Execute o script create_sales_table.sql no banco de dados.';
      } else if (error.message?.includes('permission denied')) {
        errorMessage = 'Sem permissão para criar venda. Verifique as políticas RLS no Supabase.';
      } else if (error.message) {
        errorMessage = `Erro: ${error.message}`;
      }
      
      toast.error(errorMessage);
    } finally {
      setProcessingPayment(false);
    }
  };

  // Função para atualizar estatísticas do caixa
  const updateCashRegisterStats = async (saleData) => {
    try {
      console.log('🔄 updateCashRegisterStats called with:', saleData);
      console.log('🏪 Current cash register before update:', currentCashRegister);
      
      const paymentMethod = saleData.payment_method;
      const totalAmount = parseFloat(saleData.total_amount);
      
      console.log('💳 Payment method:', paymentMethod, 'Amount:', totalAmount);
      
      // Calcular os novos valores baseados no método de pagamento
      const updateData = {
        total_sales: parseFloat(currentCashRegister.total_sales || 0) + totalAmount,
        sales_count: parseInt(currentCashRegister.sales_count || 0) + 1
      };
      
      // Atualizar contadores específicos por método de pagamento
      switch (paymentMethod) {
        case 'cash':
          updateData.cash_sales = parseFloat(currentCashRegister.cash_sales || 0) + totalAmount;
          updateData.cash_sales_count = parseInt(currentCashRegister.cash_sales_count || 0) + 1;
          console.log('💰 Updating cash sales:', updateData.cash_sales, updateData.cash_sales_count);
          break;
        case 'card':
          updateData.card_sales = parseFloat(currentCashRegister.card_sales || 0) + totalAmount;
          updateData.card_sales_count = parseInt(currentCashRegister.card_sales_count || 0) + 1;
          console.log('💳 Updating card sales:', updateData.card_sales, updateData.card_sales_count);
          break;
        case 'pix':
          updateData.pix_sales = parseFloat(currentCashRegister.pix_sales || 0) + totalAmount;
          updateData.pix_sales_count = parseInt(currentCashRegister.pix_sales_count || 0) + 1;
          console.log('📱 Updating PIX sales:', updateData.pix_sales, updateData.pix_sales_count);
          break;
        case 'student_balance':
          updateData.balance_sales = parseFloat(currentCashRegister.balance_sales || 0) + totalAmount;
          updateData.balance_sales_count = parseInt(currentCashRegister.balance_sales_count || 0) + 1;
          console.log('🎓 Updating balance sales:', updateData.balance_sales, updateData.balance_sales_count);
          break;
        default:
          console.warn('⚠️ Unknown payment method:', paymentMethod);
      }
      
      console.log('📊 Update data to be sent:', updateData);
      console.log('🆔 Cash register ID:', currentCashRegister.id);
      
      // Verificar se temos ID do caixa
      if (!currentCashRegister.id) {
        console.error('❌ No cash register ID found!');
        throw new Error('Cash register ID is missing');
      }
      
      // Atualizar o caixa no banco de dados
      console.log('🔄 Calling dataHelpers.updateCashRegister...');
      const updatedRegister = await dataHelpers.updateCashRegister(currentCashRegister.id, updateData);
      console.log('✅ Cash register updated in database:', updatedRegister);
      
      // Atualizar estado local
      const newState = {
        ...currentCashRegister,
        ...updateData
      };
      console.log('📝 Updating local state to:', newState);
      setCurrentCashRegister(newState);
      
      console.log('✅ Cash register stats update completed successfully');
      
      // Verificar se a atualização foi persistida
      setTimeout(async () => {
        try {
          const refreshedRegister = await dataHelpers.getCurrentCashRegister(company.id);
          console.log('🔍 Verified cash register after update:', refreshedRegister);
        } catch (error) {
          console.error('❌ Error verifying cash register update:', error);
        }
      }, 1000);
      
    } catch (error) {
      console.error('❌ Error updating cash register stats:', error);
      console.error('❌ Error details:', {
        message: error.message,
        stack: error.stack,
        saleData,
        currentCashRegister
      });
      // Não falhar a venda por causa disso, apenas logar o erro
      toast.error('Aviso: Erro ao atualizar estatísticas do caixa, mas venda foi registrada');
    }
  };

  // Função para imprimir comprovante
  const printVoucher = async (saleData, student) => {
    try {
      const voucherContent = generateVoucherHTML(saleData, student);
      // Criar uma nova janela para impressão
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        toast.error('Não foi possível abrir a janela de impressão. Desative o bloqueador de pop-ups.');
        return;
      }
      printWindow.document.write(voucherContent);
      printWindow.document.close();
      // Aguardar o carregamento e imprimir
      printWindow.onload = () => {
        printWindow.print();
        printWindow.close();
      };
    } catch (error) {
      console.error('Error printing voucher:', error);
      toast.error('Erro ao imprimir comprovante');
    }
  };

  // Função para gerar HTML do comprovante otimizado para impressora térmica 80mm
  const generateVoucherHTML = (saleData, student) => {
    const now = new Date();
    const paymentMethodLabel = {
      'cash': 'DINHEIRO',
      'card': 'CARTAO',
      'pix': 'PIX',
      'student_balance': 'SALDO ALUNO'
    }[saleData.payment_method] || saleData.payment_method;

    // Função para centralizar texto
    const centerText = (text, width = 32) => {
      const padding = Math.max(0, Math.floor((width - text.length) / 2));
      return ' '.repeat(padding) + text;
    };

    // Função para formatar linha de item
    const formatItemLine = (left, right, width = 32) => {
      const rightStr = right.toString();
      const leftStr = left.toString();
      const dots = Math.max(1, width - leftStr.length - rightStr.length);
      return leftStr + '.'.repeat(dots) + rightStr;
    };

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Comprovante - ${now.getTime()}</title>
        <meta charset="utf-8">
        <style>
          @media print {
            body { 
              margin: 0 !important; 
              padding: 2mm !important; 
              font-family: 'Courier New', monospace; 
              font-size: 9px !important;
              line-height: 1.1 !important;
              width: 76mm !important;
            }
            @page { 
              size: 80mm auto; 
              margin: 0 !important; 
            }
            .no-print { display: none !important; }
          }
          
          body { 
            width: 76mm; 
            margin: 0; 
            padding: 2mm;
            font-family: 'Courier New', 'Lucida Console', Monaco, monospace; 
            font-size: 9px;
            line-height: 1.1;
            color: #000;
            background: white;
          }
          
          .header { 
            text-align: center; 
            border-bottom: 1px dashed #000; 
            padding-bottom: 3mm; 
            margin-bottom: 3mm; 
          }
          
          .company-name { 
            font-weight: bold; 
            font-size: 11px; 
            margin-bottom: 1mm;
            text-transform: uppercase;
          }
          
          .voucher-title {
            font-weight: bold;
            font-size: 10px;
            margin: 1mm 0;
          }
          
          .datetime {
            font-size: 8px;
            margin-top: 1mm;
          }
          
          .section {
            margin: 2mm 0;
            font-size: 9px;
          }
          
          .item-row {
            display: flex;
            justify-content: space-between;
            margin: 0.5mm 0;
            font-size: 8px;
          }
          
          .item-name {
            font-weight: bold;
            margin-bottom: 0.5mm;
            word-wrap: break-word;
            max-width: 100%;
          }
          
          .item-details {
            display: flex;
            justify-content: space-between;
            font-size: 8px;
          }
          
          .separator { 
            border-bottom: 1px dashed #000; 
            margin: 2mm 0; 
          }
          
          .total-section {
            border-top: 1px solid #000;
            border-bottom: 1px solid #000;
            padding: 1mm 0;
            margin: 2mm 0;
            font-weight: bold;
            text-align: center;
          }
          
          .total-line {
            display: flex;
            justify-content: space-between;
            font-weight: bold;
            font-size: 10px;
          }
          
          .payment-info {
            margin: 2mm 0;
            font-size: 8px;
          }
          
          .footer { 
            text-align: center; 
            margin-top: 4mm; 
            font-size: 7px;
            border-top: 1px dashed #000;
            padding-top: 2mm;
          }
          
          .center { text-align: center; }
          .bold { font-weight: bold; }
          .small { font-size: 7px; }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="company-name">${(company?.name || 'CANTINATECH').substring(0, 30)}</div>
          <div class="voucher-title">CUPOM NAO FISCAL</div>
          <div class="datetime">${now.toLocaleDateString('pt-BR')} ${now.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</div>
        </div>
        
        <div class="section">
          <div class="bold">CLIENTE:</div>
          <div>${student ? student.nome.substring(0, 30) : 'CONSUMIDOR FINAL'}</div>
          ${student ? `<div class="small">Mat: ${(student.registration_number || 'N/A').substring(0, 15)} | ${(student.class_name || 'N/A').substring(0, 10)}</div>` : ''}
        </div>
        
        <div class="separator"></div>
        
        <div class="section">
          <div class="bold center">ITENS</div>
          ${cart.map(item => `
            <div style="margin: 1mm 0;">
              <div class="item-name">${item.nome.substring(0, 28)}</div>
              <div class="item-details">
                <span>${item.quantidade}x ${formatCurrency(item.preco).replace('R$', 'R$').substring(0, 10)}</span>
                <span class="bold">${formatCurrency(item.preco * item.quantidade).replace('R$', 'R$')}</span>
              </div>
            </div>
          `).join('')}
        </div>
        
        <div class="total-section">
          <div class="total-line">
            <span>TOTAL:</span>
            <span>${formatCurrency(saleData.total_amount).replace('R$', 'R$')}</span>
          </div>
        </div>
        
        <div class="payment-info">
          <div class="bold">PAGAMENTO: ${paymentMethodLabel}</div>
          
          ${saleData.payment_method === 'cash' ? `
            <div>Recebido: ${formatCurrency(saleData.amount_paid).replace('R$', 'R$')}</div>
            ${saleData.change_amount > 0 ? `<div>Troco: ${formatCurrency(saleData.change_amount).replace('R$', 'R$')}</div>` : ''}
          ` : ''}
          
          ${student && saleData.payment_method === 'student_balance' ? `
            <div class="small">
              <div>Saldo Anterior: ${formatCurrency(student.saldo).replace('R$', 'R$')}</div>
              <div>Saldo Atual: ${formatCurrency((student.saldo || 0) - saleData.total_amount).replace('R$', 'R$')}</div>
            </div>
          ` : ''}
        </div>
        
        <div class="footer">
          <div class="small">Op: ${(person?.name || 'Sistema').substring(0, 20)}</div>
          ${currentCashRegister ? `<div class="small">Cx: ${currentCashRegister.id.substring(0, 8).toUpperCase()}</div>` : ''}
          <div style="margin-top: 2mm;">OBRIGADO PELA PREFERENCIA!</div>
          <div class="small" style="margin-top: 1mm;">www.cantinatech.com.br</div>
        </div>
        
        <!-- Botão para fechar após impressão (não imprime) -->
        <div class="no-print" style="margin-top: 10mm; text-align: center;">
          <button onclick="window.print(); setTimeout(() => window.close(), 500);" 
                  style="padding: 5px 15px; font-size: 12px; cursor: pointer;">
            🖨️ Imprimir e Fechar
          </button>
        </div>
      </body>
      </html>
    `;
  };

  // Calculadora de troco
  const calculateChange = () => {
    const received = parseFloat(cashReceived) || 0;
    const total = getTotal();
    return received - total;
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatTime = (time) => {
    return new Intl.DateTimeFormat('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    }).format(time);
  };

  // 1. Foco automático ao trocar tipo de busca (Nome, Cartão, Mat.)
  const handleSearchTypeChange = (key) => {
    setSearchType(key);
    setTimeout(() => {
      searchInputRef.current?.focus();
    }, 100);
  };

  return (
    <div className="h-screen flex flex-col bg-gradient-to-br from-slate-50 to-blue-50 overflow-hidden">
      {/* Header Moderno */}
      <div className="flex-shrink-0 bg-white/80 backdrop-blur-md border-b border-gray-200/50 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-6 h-6 text-gray-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                PDV Moderno
              </h1>
              <p className="text-sm text-gray-500">Sistema de vendas otimizado</p>
            </div>
          </div>

          <div className="flex items-center space-x-6">
            {/* Stats rápidas */}
            <div className="hidden md:flex items-center space-x-6">
              <div className="text-center">
                <p className="text-2xl font-bold text-green-600">{cart.length}</p>
                <p className="text-xs text-gray-500">Itens</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-blue-600">{formatCurrency(getTotal())}</p>
                <p className="text-xs text-gray-500">Total</p>
              </div>
              
              {/* Status do Caixa */}
              <div className="text-center border-l border-gray-200 pl-6">
                <div className="flex items-center space-x-2">
                  {currentCashRegister ? (
                    <>
                      <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                      <span className="text-sm font-medium text-green-600">Caixa Aberto</span>
                    </>
                  ) : (
                    <>
                      <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                      <span className="text-sm font-medium text-red-600">Caixa Fechado</span>
                    </>
                  )}
                </div>
                {currentCashRegister && (
                  <p className="text-xs text-gray-500 mt-1">
                    Vendas: {formatCurrency(currentCashRegister.total_sales || 0)}
                  </p>
                )}
              </div>
            </div>

            {/* Controles de impressão e caixa */}
            <div className="flex items-center space-x-2">
              {/* Botão reimprimir último comprovante */}
              {lastSaleData && (
                <button
                  onClick={() => printVoucher(lastSaleData, lastStudent)}
                  className="px-3 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors flex items-center space-x-2"
                  title="Reimprimir último comprovante"
                >
                  <Receipt className="w-4 h-4" />
                  <span className="hidden lg:inline">Reimprimir</span>
                </button>
              )}
              
              {/* Toggle impressão automática */}
              <button
                onClick={() => setPrintVoucherEnabled(!printVoucherEnabled)}
                className={`px-3 py-2 rounded-lg transition-colors flex items-center space-x-2 ${
                  printVoucherEnabled 
                    ? 'bg-green-600 text-white hover:bg-green-700' 
                    : 'bg-red-600 text-white hover:bg-red-700'
                }`}
                title={`Impressão automática: ${printVoucherEnabled ? 'Ligada' : 'Desligada'}`}
              >
                <Receipt className="w-4 h-4" />
                <span className="hidden lg:inline">
                  {printVoucherEnabled ? 'Auto-Print ON' : 'Auto-Print OFF'}
                </span>
              </button>
              
              {/* Botão para abrir caixa */}
              {!currentCashRegister && (
                <button
                  onClick={() => navigate('/dashboard/cashier')}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2"
                >
                  <DollarSign className="w-4 h-4" />
                  <span>Abrir Caixa</span>
                </button>
              )}
            </div>

            <div className="text-right">
              <p className="text-lg font-mono text-gray-900">{formatTime(currentTime)}</p>
              <p className="text-sm text-gray-500">                {currentTime.toLocaleDateString('pt-BR', { weekday: 'short', day: '2-digit', month: 'short' })}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 flex min-h-0">        {/* Painel Principal - Produtos e Busca */}
        <div className="flex-1 flex flex-col p-6 space-y-6">
          {/* Categorias */}
          <div className="flex space-x-3 overflow-x-auto pb-2">
            {categories.map((category) => {
              const Icon = category.icon;
              return (
                <button
                  key={category.name}
                  onClick={() => setSelectedCategory(category.name)}
                  className={`flex-shrink-0 flex items-center px-6 py-3 rounded-xl font-medium transition-all duration-200 ${
                    selectedCategory === category.name
                      ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
                      : 'bg-white/80 text-gray-700 hover:bg-white border border-gray-200'
                  }`}
                >
                  <Icon className="w-5 h-5 mr-2" />
                  {category.name}
                </button>
              );
            })}
          </div>

          {/* Grid de Produtos */}
          <div className="flex-1 overflow-hidden">
            <div className="h-full overflow-y-auto">              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6 pb-4">
                {filteredProducts.map((product) => (                  <button
                    key={product.id}
                    onClick={() => addToCart(product)}
                    className={`group relative bg-white/90 backdrop-blur-md rounded-2xl border p-5 hover:shadow-2xl hover:scale-[1.02] hover:-translate-y-1 transition-all duration-300 overflow-hidden isolate ${
                      (product.stock_quantity || 0) <= 5 
                        ? 'border-red-200/70 hover:shadow-red-500/10 animate-pulse-glow' 
                        : 'border-gray-200/50 hover:shadow-blue-500/10'
                    }`}
                  >{/* Gradiente de fundo sutil */}
                    <div className={`absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 product-card-bg ${
                      (product.stock_quantity || 0) <= 5
                        ? 'bg-gradient-to-br from-red-50/50 via-white to-orange-50/30'
                        : 'bg-gradient-to-br from-blue-50/50 via-white to-purple-50/30'
                    }`} />
                    
                    {/* Badge de categoria */}
                    <div className={`absolute top-3 right-3 text-white text-xs px-2 py-1 rounded-full font-medium opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-x-2 group-hover:translate-x-0 product-card-badges bg-gradient-to-r from-blue-500 to-purple-500`}>
                      {product.category || 'Produto'}
                    </div>

                    {/* Alerta de estoque baixo */}
                    {(product.stock_quantity || 0) <= 5 && (
                      <div className="absolute top-3 left-3 bg-gradient-to-r from-red-500 to-orange-500 text-white text-xs px-2 py-1 rounded-full font-medium animate-pulse product-card-badges">
                        ⚠️ Baixo
                      </div>
                    )}                    {/* Imagem ou ícone do produto */}
                    <div className={`relative h-32 rounded-xl mb-4 flex items-center justify-center transition-all duration-300 product-card-image ${
                      (product.stock_quantity || 0) <= 5
                        ? 'bg-gradient-to-br from-red-100 via-orange-50 to-red-50 group-hover:bg-gradient-to-br group-hover:from-red-200 group-hover:via-orange-100 group-hover:to-red-100'
                        : 'bg-gradient-to-br from-gray-100 via-gray-50 to-blue-50 group-hover:bg-gradient-to-br group-hover:from-blue-100 group-hover:via-purple-50 group-hover:to-blue-100'
                    }`}>
                      {product.image_url ? (
                        <img
                          src={product.image_url}
                          alt={product.name}
                          className="w-full h-full object-cover rounded-xl group-hover:scale-110 transition-transform duration-300"
                        />
                      ) : (
                        <div className="text-4xl mb-2 group-hover:scale-110 transition-transform duration-300 relative z-10">
                          📦
                        </div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/5 to-transparent rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-5" />
                    </div>

                    {/* Informações do produto */}
                    <div className="relative product-card-content text-left">
                      <h4 className="font-semibold text-gray-900 mb-2 text-sm leading-tight group-hover:text-blue-900 transition-colors duration-300">
                        {product.name}
                      </h4>
                      
                      <p className="text-xs text-gray-600 mb-3 line-clamp-2 group-hover:text-gray-700 transition-colors duration-300">
                        {product.description || 'Produto disponível'}
                      </p>

                      {/* Preço com destaque */}
                      <div className="flex items-center justify-between mb-2">
                        <div className="bg-gradient-to-r from-green-500 to-emerald-500 text-white px-3 py-1.5 rounded-lg shadow-lg group-hover:shadow-green-500/25 transition-all duration-300">
                          <span className="text-lg font-bold">
                            {formatCurrency(product.price)}
                          </span>
                        </div>
                        
                        {/* Indicador de estoque */}
                        <div className={`flex items-center space-x-1 ${
                          (product.stock_quantity || 0) > 10 
                            ? 'text-green-600' 
                            : (product.stock_quantity || 0) > 5 
                              ? 'text-yellow-600' 
                              : 'text-red-600'
                        }`}>
                          <div className={`w-2 h-2 rounded-full ${
                            (product.stock_quantity || 0) > 10 
                              ? 'bg-green-500' 
                              : (product.stock_quantity || 0) > 5 
                                ? 'bg-yellow-500' 
                                : 'bg-red-500 animate-pulse'
                          }`} />
                          <span className="text-xs font-medium">
                            {product.stock_quantity || 0}
                          </span>
                        </div>
                      </div>
                    </div>                    {/* Efeito de brilho no hover */}
                    <div className="absolute top-0 -left-4 w-24 h-full bg-gradient-to-r from-transparent via-white/20 to-transparent skew-x-12 opacity-0 group-hover:opacity-100 group-hover:animate-shimmer transition-opacity duration-700 product-card-shimmer" />
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Carrinho Lateral */}
        <div className="w-96 bg-white/90 backdrop-blur-md border-l border-gray-200/50 flex flex-col shadow-2xl">
          {/* Header do Carrinho */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-semibold text-gray-900">Carrinho</h3>
              {cart.length > 0 && (
                <button
                  onClick={clearCart}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              )}
            </div>
          </div>

          {/* Itens do Carrinho */}
          <div className="flex-1 overflow-y-auto p-6">
            {cart.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-gray-500">
                <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                  <ShoppingCart className="w-12 h-12" />
                </div>
                <p className="text-lg font-medium">Carrinho vazio</p>
                <p className="text-sm">Adicione produtos para continuar</p>
              </div>
            ) : (
              <div className="space-y-4">
                {cart.map((item) => (
                  <div key={item.id} className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium text-gray-900 text-sm">{item.nome}</h4>
                      <button
                        onClick={() => removeFromCart(item.id)}
                        className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <button
                          onClick={() => updateQuantity(item.id, item.quantidade - 1)}
                          className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors"
                        >
                          <Minus className="w-4 h-4" />
                        </button>
                        <span className="w-8 text-center font-medium">{item.quantidade}</span>
                        <button
                          onClick={() => updateQuantity(item.id, item.quantidade + 1)}
                          className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors"
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                      
                      <div className="text-right">
                        <p className="text-sm text-gray-600">
                          {formatCurrency(item.preco)} × {item.quantidade}
                        </p>
                        <p className="font-semibold text-gray-900">
                          {formatCurrency(item.preco * item.quantidade)}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>          {/* Finalização */}
          {cart.length > 0 && (
            <div className="border-t border-gray-200 p-6 space-y-4">
              {/* Total */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <span className="text-lg font-semibold text-gray-900">Total:</span>
                  <span className="text-2xl font-bold text-green-600">
                    {formatCurrency(getTotal())}
                  </span>
                </div>
              </div>
              {/* Botão Finalizar */}
              <button
                onClick={finalizeSale}
                className="w-full py-4 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl font-semibold text-lg hover:from-green-600 hover:to-green-700 transition-all duration-200 flex items-center justify-center"
              >
                <Receipt className="w-5 h-5 mr-2" />
                Finalizar Venda
              </button>
            </div>
          )}
        </div>
      </div>      {/* Modal de Finalização da Venda */}
      {showConfirmation && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[95vh] overflow-hidden shadow-2xl flex flex-col">
            {/* Header */}
            <div className="border-b border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-gray-900">Finalizar Venda</h3>
                <button
                  onClick={() => setShowConfirmation(false)}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
            </div>            <div className="flex-1 overflow-y-auto">
              <div className="grid grid-cols-2 gap-4 p-4">
                {/* Coluna Esquerda - Cliente e Pagamento */}
                <div className="space-y-4">
                  {/* Seleção de Cliente */}
                  <div className="bg-gradient-to-r from-slate-50 to-blue-50 rounded-xl p-3 border border-gray-200">
                    <h4 className="font-semibold text-base mb-3">Cliente da Venda</h4>
                    
                    {/* Tipo de Cliente */}
                    <div className="grid grid-cols-2 gap-2 mb-3">
                      <button
                        onClick={() => {
                          setSelectedStudent(null);
                          setPaymentMethod('dinheiro');
                        }}
                        className={`p-3 rounded-lg border-2 transition-all text-sm ${
                          !selectedStudent
                            ? 'border-green-500 bg-green-50 text-green-700'
                            : 'border-gray-200 text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <User className="w-5 h-5 mx-auto mb-1" />
                        <div className="font-medium">Consumidor Final</div>
                        <div className="text-xs mt-1">Venda avulsa</div>
                      </button>
                      <button
                        onClick={() => setPaymentMethod('saldo')}
                        className={`p-3 rounded-lg border-2 transition-all text-sm ${
                          selectedStudent
                            ? 'border-blue-500 bg-blue-50 text-blue-700'
                            : 'border-gray-200 text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <CreditCard className="w-5 h-5 mx-auto mb-1" />
                        <div className="font-medium">Aluno</div>
                        <div className="text-xs mt-1">Com carteirinha</div>
                      </button>
                    </div>

                    {/* Busca de Aluno (somente se não for consumidor final) */}
                    {paymentMethod === 'saldo' && (
                      <div className="space-y-2">
                        <div className="flex bg-gray-100 rounded-lg p-1">
                          {[
                            { key: 'nome', label: 'Nome', icon: User },
                            { key: 'carteirinha', label: 'Cartão', icon: CreditCard },
                            { key: 'matricula', label: 'Mat.', icon: Hash }
                          ].map(({ key, label, icon: Icon }) => (
                            <button
                              key={key}
                              onClick={() => handleSearchTypeChange(key)}
                              className={`flex items-center px-2 py-1.5 rounded-md text-xs font-medium transition-all duration-200 flex-1 ${
                                searchType === key
                                  ? 'bg-white text-blue-600 shadow-sm'
                                  : 'text-gray-600 hover:text-gray-900'
                              }`}
                            >
                              <Icon className="w-3 h-3 mr-1" />
                              {label}
                            </button>
                          ))}
                        </div>

                        <div className="relative">
                          <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                          <input
                            ref={searchInputRef}
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder={`Buscar por ${searchType}...`}
                            className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />

                          {/* Resultados da busca */}
                          {searchResults.length > 0 && (
                            <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-32 overflow-y-auto">
                              {searchResults.map((student) => (
                                <button
                                  key={student.id}
                                  onClick={() => selectStudent(student)}
                                  className="w-full p-2 text-left hover:bg-blue-50 border-b border-gray-100 last:border-b-0 transition-colors text-sm"
                                >
                                  <div className="flex items-center justify-between">
                                    <div>
                                      <p className="font-medium text-gray-900">{student.nome}</p>
                                      <p className="text-xs text-gray-600">
                                        {student.turma} • {student.carteirinha}
                                      </p>
                                    </div>
                                    <div className="text-right">
                                      <p className="font-semibold text-green-600 text-sm">
                                        {formatCurrency(student.saldo)}
                                      </p>
                                    </div>
                                  </div>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Aluno selecionado */}
                        {selectedStudent && (
                          <div className="bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg p-3 text-white">
                            <div className="flex items-center justify-between">
                              <div>
                                <h5 className="font-semibold text-sm">{selectedStudent.nome}</h5>
                                <p className="text-blue-100 text-xs">
                                  {selectedStudent.turma} • {selectedStudent.carteirinha}
                                </p>
                              </div>
                              <div className="text-right">
                                <p className="text-lg font-bold">{formatCurrency(selectedStudent.saldo)}</p>
                                <p className="text-blue-100 text-xs">Saldo</p>
                              </div>
                              <button
                                onClick={() => {
                                  setSelectedStudent(null);
                                  setSearchTerm('');
                                  setSearchResults([]);
                                }}
                                className="ml-2 p-1 text-white/80 hover:text-white hover:bg-white/20 rounded-full transition-colors"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Forma de Pagamento */}
                  <div>
                    <h4 className="font-semibold text-base mb-2">Forma de Pagamento</h4>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        onClick={() => setPaymentMethod('saldo')}
                        disabled={!selectedStudent}
                        className={`p-3 rounded-lg border-2 transition-all text-sm ${
                          paymentMethod === 'saldo'
                            ? 'border-blue-500 bg-blue-50 text-blue-700'
                            : selectedStudent 
                              ? 'border-gray-200 text-gray-700 hover:bg-gray-50'
                              : 'border-gray-200 text-gray-400 cursor-not-allowed'
                        }`}
                      >
                        <Zap className="w-6 h-6 mx-auto mb-1" />
                        <div className="font-medium">Saldo</div>
                        <div className="text-xs mt-1">
                          {selectedStudent 
                            ? `${formatCurrency(selectedStudent.saldo)}`
                            : 'Selecione aluno'
                          }
                        </div>
                      </button>
                      <button
                        onClick={() => setPaymentMethod('dinheiro')}
                        className={`p-3 rounded-lg border-2 transition-all text-sm ${
                          paymentMethod === 'dinheiro'
                            ? 'border-blue-500 bg-blue-50 text-blue-700'
                            : 'border-gray-200 text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <DollarSign className="w-6 h-6 mx-auto mb-1" />
                        <div className="font-medium">Dinheiro</div>
                        <div className="text-xs mt-1">Em espécie</div>
                      </button>
                      <button
                        onClick={() => setPaymentMethod('cartao')}
                        className={`p-3 rounded-lg border-2 transition-all text-sm ${
                          paymentMethod === 'cartao'
                            ? 'border-blue-500 bg-blue-50 text-blue-700'
                            : 'border-gray-200 text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <CreditCard className="w-6 h-6 mx-auto mb-1" />
                        <div className="font-medium">Cartão</div>
                        <div className="text-xs mt-1">Débito/Crédito</div>
                      </button>
                      <button
                        onClick={() => setPaymentMethod('pix')}
                        className={`p-3 rounded-lg border-2 transition-all text-sm ${
                          paymentMethod === 'pix'
                            ? 'border-blue-500 bg-blue-50 text-blue-700'
                            : 'border-gray-200 text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <QrCode className="w-6 h-6 mx-auto mb-1" />
                        <div className="font-medium">PIX</div>
                        <div className="text-xs mt-1">Instantâneo</div>
                      </button>
                    </div>
                  </div>

                  {/* Pagamento em Dinheiro */}
                  {paymentMethod === 'dinheiro' && (
                    <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 rounded-lg p-3">
                      <div className="flex items-center mb-2">
                        <DollarSign className="w-4 h-4 text-yellow-600 mr-2" />
                        <label className="text-sm font-medium text-gray-700">
                          Valor Recebido:
                        </label>
                      </div>
                      <div className="relative">
                        <span className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">R$</span>
                        <input
                          type="number"
                          step="0.01"
                          value={cashReceived}
                          onChange={(e) => setCashReceived(e.target.value)}
                          placeholder="0,00"
                          className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                      
                      {cashReceived && (
                        <div className="mt-2 p-2 bg-white rounded-lg border">
                          {parseFloat(cashReceived) >= getTotal() ? (
                            <div className="flex items-center justify-between text-green-700">
                              <div className="flex items-center">
                                <CheckCircle className="w-4 h-4 mr-1" />
                                <span className="font-medium text-sm">Troco:</span>
                              </div>
                              <span className="text-lg font-bold">{formatCurrency(calculateChange())}</span>
                            </div>
                          ) : (
                            <div className="flex items-center text-red-700">
                              <AlertCircle className="w-4 h-4 mr-1" />
                              <span className="font-medium text-sm">
                                Falta: {formatCurrency(getTotal() - parseFloat(cashReceived))}
                              </span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Pagamento PIX */}
                  {paymentMethod === 'pix' && (
                    <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg p-3">
                      <div className="flex items-center mb-2">
                        <QrCode className="w-4 h-4 text-purple-600 mr-2" />
                        <span className="text-sm font-medium text-gray-700">
                          Pagamento PIX
                        </span>
                      </div>                      <div className="text-sm text-gray-600">
                        {pixPaymentData && pixStatus === 'pending' ? (
                          <div className="flex items-center text-blue-600">
                            <Clock className="w-4 h-4 mr-1" />
                            <span>PIX pendente será restaurado</span>
                          </div>
                        ) : (
                          <span>QR Code será gerado após confirmação</span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Validações */}
                  {paymentMethod === 'saldo' && !selectedStudent && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                      <div className="flex items-center">
                        <AlertCircle className="w-4 h-4 text-yellow-600 mr-2" />
                        <div>
                          <p className="font-medium text-yellow-800 text-sm">Selecione um Aluno</p>
                          <p className="text-xs text-yellow-700">
                            Necessário para pagamento com saldo.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {paymentMethod === 'saldo' && selectedStudent && selectedStudent.saldo < getTotal() && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                      <div className="flex items-center">
                        <AlertCircle className="w-4 h-4 text-red-600 mr-2" />
                        <div>
                          <p className="font-medium text-red-800 text-sm">Saldo Insuficiente</p>
                          <p className="text-xs text-red-700">
                            Faltam {formatCurrency(getTotal() - selectedStudent.saldo)}.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Informações Pós-Venda */}
                  {paymentMethod === 'saldo' && selectedStudent && selectedStudent.saldo >= getTotal() && (
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-3">
                      <div className="flex items-center mb-2">
                        <Zap className="w-4 h-4 text-blue-600 mr-2" />
                        <h5 className="font-medium text-blue-800 text-sm">Situação Pós-Venda:</h5>
                      </div>
                      <div className="grid grid-cols-2 gap-3 text-xs">
                        <div>
                          <p className="text-blue-600 mb-1">Saldo Atual</p>
                          <p className="font-bold text-blue-800">{formatCurrency(selectedStudent.saldo)}</p>
                        </div>
                        <div>
                          <p className="text-blue-600 mb-1">Saldo Restante</p>
                          <p className="font-bold text-green-700">{formatCurrency(selectedStudent.saldo - getTotal())}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Coluna Direita - Itens e Total */}
                <div className="space-y-4">
                  {/* Itens da Venda */}
                  <div>
                    <h4 className="font-semibold text-base mb-2">Itens da Venda</h4>
                    <div className="bg-gray-50 rounded-lg p-3 max-h-64 overflow-y-auto">
                      <div className="space-y-2">
                        {cart.map((item) => (
                          <div key={item.id} className="flex items-center justify-between bg-white rounded-lg p-2">
                            <div className="flex-1">
                              <p className="font-medium text-gray-900 text-sm">{item.nome}</p>
                              <p className="text-xs text-gray-600">{item.descricao}</p>
                            </div>
                            <div className="text-center mx-2">
                              <p className="text-xs text-gray-600">Qtd</p>
                              <p className="font-semibold text-sm">{item.quantidade}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-xs text-gray-600">{formatCurrency(item.preco)} × {item.quantidade}</p>
                              <p className="font-semibold text-gray-900 text-sm">{formatCurrency(item.preco * item.quantidade)}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Resumo Financeiro */}
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-gray-600 text-sm">Subtotal ({cart.reduce((total, item) => total + item.quantidade, 0)} itens):</span>
                      <span className="font-semibold">{formatCurrency(getTotal())}</span>
                    </div>
                    <div className="flex items-center justify-between text-xl font-bold text-green-700 border-t border-green-200 pt-2">
                      <div className="flex items-center">
                        <Receipt className="w-5 h-5 mr-2" />
                        <span>Total:</span>
                      </div>
                      <span>{formatCurrency(getTotal())}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>            {/* Footer com botões */}
            <div className="border-t border-gray-200 p-4 bg-gray-50/50">
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowConfirmation(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors flex items-center justify-center"
                >
                  <X className="w-4 h-4 mr-2" />
                  Cancelar
                </button>                <button
                  onClick={confirmSale}
                  disabled={
                    (paymentMethod === 'saldo' && (!selectedStudent || selectedStudent.saldo < getTotal())) ||
                    (paymentMethod === 'dinheiro' && (!cashReceived || parseFloat(cashReceived) < getTotal())) ||
                    (cart.length === 0) ||
                    isGeneratingPix
                  }
                  className="flex-1 px-4 py-2 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg font-medium hover:from-green-600 hover:to-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center shadow-lg"
                >
                  {isGeneratingPix ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                      Gerando PIX...
                    </>
                  ) : paymentMethod === 'pix' ? (
                    <>
                      <QrCode className="w-4 h-4 mr-2" />
                      Gerar PIX
                    </>
                  ) : (
                    <>
                      <Receipt className="w-4 h-4 mr-2" />
                      Confirmar Venda
                    </>
                  )}
                  {paymentMethod === 'dinheiro' && cashReceived && parseFloat(cashReceived) >= getTotal() && (
                    <span className="ml-2 text-xs">
                      (Troco: {formatCurrency(calculateChange())})
                    </span>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>      )}

      {/* Modal PIX */}
      {showPixModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-2xl max-w-md w-full max-h-[95vh] overflow-hidden shadow-2xl flex flex-col">
            {/* Header */}
            <div className="border-b border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <QrCode className="w-6 h-6 text-purple-600 mr-2" />
                  <h3 className="text-xl font-semibold text-gray-900">Pagamento PIX</h3>
                </div>
                <button
                  onClick={cancelPix}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {/* Resumo da Venda */}
              <div className="bg-gradient-to-r from-slate-50 to-blue-50 rounded-lg p-3 border border-gray-200">
                <h4 className="font-semibold text-sm text-gray-800 mb-2">Resumo da Venda</h4>
                <div className="space-y-1">
                  {cart.map((item, index) => (
                    <div key={index} className="flex justify-between text-xs text-gray-600">
                      <span>{item.quantidade}x {item.nome}</span>
                      <span>{formatCurrency(item.preco * item.quantidade)}</span>
                    </div>
                  ))}
                  <div className="border-t border-gray-300 pt-1 flex justify-between font-semibold text-sm">
                    <span>Total:</span>
                    <span className="text-green-600">{formatCurrency(getTotal())}</span>
                  </div>
                </div>
              </div>

              {/* Status do PIX */}
              {isGeneratingPix && (
                <div className="text-center py-8">
                  <div className="inline-block animate-spin text-purple-600 mb-4">
                    <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
                    </svg>
                  </div>
                  <p className="text-gray-600">Gerando PIX...</p>
                </div>
              )}

              {pixError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center">
                    <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
                    <div>
                      <p className="font-medium text-red-800 text-sm">Erro ao gerar PIX</p>
                      <p className="text-red-700 text-xs mt-1">{pixError}</p>
                    </div>
                  </div>
                </div>
              )}

              {pixPaymentData && pixStatus === 'pending' && (
                <div className="space-y-4">                  {/* QR Code */}
                  {(pixPaymentData.encodedImage || pixPaymentData.payload) && (
                    <div className="text-center">
                      <div className="bg-white p-4 rounded-lg border border-gray-200 inline-block">
                        {pixPaymentData.payload ? (
                          <QRCodeGenerator 
                            value={pixPaymentData.payload}
                            size={192}
                            className="rounded-lg"
                          />
                        ) : (
                          <img
                            src={`data:image/png;base64,${pixPaymentData.encodedImage}`}
                            alt="QR Code PIX"
                            className="w-48 h-48 mx-auto"
                          />
                        )}
                      </div>
                      <p className="text-sm text-gray-600 mt-2">
                        Escaneie o QR Code com seu app de pagamentos
                      </p>
                    </div>
                  )}

                  {/* Código PIX */}
                  {pixPaymentData.payload && (
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">
                        Ou copie o código PIX:
                      </label>
                      <div className="flex">
                        <input
                          type="text"
                          value={pixPaymentData.payload}
                          readOnly
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-l-lg text-xs bg-gray-50"
                        />                        <button
                          onClick={copyPixCode}
                          className="px-4 py-2 bg-purple-600 text-white rounded-r-lg hover:bg-purple-700 transition-colors text-xs font-medium"
                        >
                          Copiar
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Status */}
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                    <div className="flex items-center">
                      <Clock className="w-5 h-5 text-yellow-600 mr-2" />
                      <div>
                        <p className="font-medium text-yellow-800 text-sm">Aguardando Pagamento</p>
                        <p className="text-yellow-700 text-xs mt-1">
                          O pagamento será confirmado automaticamente
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {pixStatus === 'paid' && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                  <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-2" />
                  <p className="font-medium text-green-800">Pagamento Confirmado!</p>
                  <p className="text-green-700 text-sm mt-1">
                    A venda será finalizada automaticamente
                  </p>
                </div>
              )}

              {pixStatus === 'expired' && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
                  <AlertCircle className="w-12 h-12 text-red-600 mx-auto mb-2" />
                  <p className="font-medium text-red-800">PIX Expirado</p>
                  <p className="text-red-700 text-sm mt-1">
                    Gere um novo PIX para continuar
                  </p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="border-t border-gray-200 p-4">
              <div className="flex space-x-3">
                <button
                  onClick={cancelPix}
                  className="flex-1 py-2 px-4 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                >
                  Cancelar PIX
                </button>
                {pixStatus === 'expired' && (
                  <button
                    onClick={generatePix}
                    disabled={isGeneratingPix}
                    className="flex-1 py-2 px-4 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium disabled:opacity-50"
                  >
                    Gerar Novo PIX
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de processamento de venda */}
      {processingPayment && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[100]">
          <div className="bg-white rounded-2xl shadow-2xl p-8 flex flex-col items-center">
            <div className="mb-4 animate-spin text-blue-600">
              <svg className="w-12 h-12" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
              </svg>
            </div>            <h2 className="text-lg font-semibold mb-2">Gerando Voucher...</h2>
            <p className="text-gray-600 text-sm">Por favor, aguarde o processamento.</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default PDVPage;